import { useState } from 'react'
import { useGetQuery } from 'hooks'
import { apiFetch } from 'actions/apiFetch'
import { spriteImagePath } from '../../games/pinmonkey/config/constants'

const sectionStyle = {
  marginTop: '1.5rem',
  borderTop: '1px solid #332211',
  paddingTop: '1rem',
}

const labelStyle = {
  fontSize: '0.7rem',
  letterSpacing: '0.15em',
  color: '#664422',
  textTransform: 'uppercase',
  marginBottom: '0.6rem',
}

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.4rem 0',
  borderBottom: '1px solid #1a1208',
  fontFamily: "'Silkscreen', monospace",
  fontSize: '0.8rem',
  color: '#e8d5a0',
}

const inputStyle = {
  width: 52,
  background: '#0e0c08',
  border: '1px solid #332211',
  color: '#e8d5a0',
  fontFamily: "'Silkscreen', monospace",
  fontSize: '0.75rem',
  padding: '0.2rem 0.4rem',
}

const selectStyle = {
  ...inputStyle,
  width: 'auto',
  minWidth: 120,
  maxWidth: 200,
}

const btnStyle = (danger) => ({
  background: 'none',
  border: `1px solid ${danger ? '#663333' : '#443322'}`,
  color: danger ? '#cc4444' : '#8a7a50',
  fontFamily: "'Silkscreen', monospace",
  fontSize: '0.72rem',
  padding: '0.2rem 0.5rem',
  cursor: 'pointer',
})

const boundaryRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.25rem 0',
  fontSize: '0.75rem',
  fontFamily: "'Silkscreen', monospace",
  color: '#c8b880',
}

const NEW_FORM_DEFAULT = { npcId: '', level: 0, x: 40, y: 40 }
const EMPTY_BOUNDARY = { x: 0, y: 0, w: 10, h: 10, hours: null }

const normalizeBoundaries = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return [raw]
}

const BoundaryEditor = ({ boundaries, onChange }) => {
  const add = () => onChange([...boundaries, { ...EMPTY_BOUNDARY }])
  const remove = (i) => onChange(boundaries.filter((_, idx) => idx !== i))
  const setField = (i, field, value) =>
    onChange(boundaries.map((b, idx) => idx === i ? { ...b, [field]: value } : b))
  const setHours = (i, hourIdx, value) => {
    const b = boundaries[i]
    const hours = b.hours ? [...b.hours] : [9, 17]
    hours[hourIdx] = parseInt(value, 10)
    onChange(boundaries.map((b2, idx) => idx === i ? { ...b2, hours } : b2))
  }
  const setSchedule = (i, mode) => {
    onChange(boundaries.map((b2, idx) =>
      idx === i ? { ...b2, hours: mode === 'scheduled' ? [9, 17] : null } : b2
    ))
  }

  const cardStyle = {
    background: '#0e0c08',
    border: '1px solid #332211',
    borderRadius: 4,
    padding: '0.6rem 0.75rem',
    marginBottom: '0.5rem',
  }
  const rowS = { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }
  const lbl = { fontSize: '0.7rem', color: '#664422', whiteSpace: 'nowrap' }
  const numInput = { ...inputStyle, width: 48 }

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ ...labelStyle, marginBottom: '0.4rem' }}>Boundary Zones</div>
      {boundaries.length === 0 && (
        <div style={{ fontSize: '0.72rem', color: '#554433', marginBottom: '0.4rem' }}>
          No boundaries — NPC roams the entire dungeon.
        </div>
      )}
      {boundaries.map((b, i) => (
        <div key={i} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.72rem', color: '#8a7a50' }}>Zone {i + 1}</span>
            <button style={btnStyle(true)} onClick={() => remove(i)}>Remove zone</button>
          </div>

          <div style={rowS}>
            <span style={lbl}>Tile position</span>
            <span style={lbl}>X</span>
            <input style={numInput} type="number" value={b.x}
              onChange={e => setField(i, 'x', parseInt(e.target.value, 10) || 0)} />
            <span style={lbl}>Y</span>
            <input style={numInput} type="number" value={b.y}
              onChange={e => setField(i, 'y', parseInt(e.target.value, 10) || 0)} />
            <span style={lbl}>W</span>
            <input style={numInput} type="number" value={b.w}
              onChange={e => setField(i, 'w', Math.max(1, parseInt(e.target.value, 10) || 1))} />
            <span style={lbl}>H</span>
            <input style={numInput} type="number" value={b.h}
              onChange={e => setField(i, 'h', Math.max(1, parseInt(e.target.value, 10) || 1))} />
          </div>

          <div style={rowS}>
            <span style={lbl}>Schedule</span>
            <select
              style={{ ...selectStyle, minWidth: 140 }}
              value={b.hours ? 'scheduled' : 'always'}
              onChange={e => setSchedule(i, e.target.value)}
            >
              <option value="always">Always active</option>
              <option value="scheduled">Specific hours</option>
            </select>
            {b.hours && (
              <>
                <span style={lbl}>From</span>
                <input style={numInput} type="number" min={0} max={23}
                  value={b.hours[0]}
                  onChange={e => setHours(i, 0, e.target.value)} />
                <span style={lbl}>:00 until</span>
                <input style={numInput} type="number" min={1} max={24}
                  value={b.hours[1]}
                  onChange={e => setHours(i, 1, e.target.value)} />
                <span style={lbl}>:00</span>
                <span style={{ fontSize: '0.68rem', color: '#554433' }}>
                  (24h, e.g. 9–17 = 9am–5pm)
                </span>
              </>
            )}
          </div>
        </div>
      ))}
      <button style={btnStyle(false)} onClick={add}>+ Add boundary zone</button>
    </div>
  )
}

export const DungeonNpcPlacements = ({ dungeonId }) => {
  const [newForm, setNewForm]     = useState(null)
  const [editing, setEditing]     = useState(null)

  const { data: placementsData, refetch } = useGetQuery({
    url: `/admin/dungeons/${dungeonId}/npcs`,
    enabled: !!dungeonId,
  })
  const { data: npcsData } = useGetQuery({ url: '/admin/npcs?limit=200' })

  const placements = placementsData?.npc_placements ?? []
  const allNpcs    = npcsData?.npcs?.rows ?? []

  const handleAdd = async () => {
    if (!newForm?.npcId) return
    await apiFetch({
      method: 'POST',
      path: `/admin/dungeons/${dungeonId}/npcs`,
      payload: {
        npc_id:      newForm.npcId,
        level_index: parseInt(newForm.level) || 0,
        x:           parseInt(newForm.x) || 0,
        y:           parseInt(newForm.y) || 0,
      }
    })
    setNewForm(null)
    refetch()
  }

  const handleUpdate = async () => {
    const boundaries = editing.boundaries
    await apiFetch({
      method: 'PUT',
      path: `/admin/dungeons/${dungeonId}/npcs/${editing.id}`,
      payload: {
        level_index: parseInt(editing.level_index) || 0,
        x:           parseInt(editing.x) || 0,
        y:           parseInt(editing.y) || 0,
        boundary:    boundaries.length > 0 ? boundaries : null,
      }
    })
    setEditing(null)
    refetch()
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this NPC from the dungeon?')) return
    await apiFetch({ method: 'DELETE', path: `/admin/dungeons/${dungeonId}/npcs/${id}` })
    refetch()
  }

  const setField = (field, value) => setEditing((f) => ({ ...f, [field]: value }))

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>NPC Placements</div>

      {placements.length === 0 && (
        <div style={{ fontSize: '0.75rem', color: '#664422', marginBottom: '0.5rem' }}>
          No NPCs placed in this dungeon yet.
        </div>
      )}

      {placements.map((p) => {
        const npc = p.Npc
        const isEditing = editing?.id === p.id
        return (
          <div key={p.id} style={{ ...rowStyle, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
              {npc?.sprite && (
                <img
                  src={spriteImagePath(npc.sprite)}
                  alt={npc.sprite}
                  style={{ width: 24, height: 24, imageRendering: 'pixelated', objectFit: 'contain', flexShrink: 0 }}
                />
              )}
              <span style={{ flex: 1 }}>{npc?.name ?? '—'}</span>
            </div>
            {isEditing ? (
              <div style={{ width: '100%', padding: '0.4rem 0' }}>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '0.7rem', color: '#664422' }}>Lv</label>
                  <input style={inputStyle} type="number" value={editing.level_index}
                    onChange={(e) => setField('level_index', e.target.value)} />
                  <label style={{ fontSize: '0.7rem', color: '#664422' }}>X</label>
                  <input style={inputStyle} type="number" value={editing.x}
                    onChange={(e) => setField('x', e.target.value)} />
                  <label style={{ fontSize: '0.7rem', color: '#664422' }}>Y</label>
                  <input style={inputStyle} type="number" value={editing.y}
                    onChange={(e) => setField('y', e.target.value)} />
                </div>
                <BoundaryEditor
                  boundaries={editing.boundaries}
                  onChange={(b) => setField('boundaries', b)}
                />
                <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                  <button style={btnStyle(false)} onClick={handleUpdate}>Save</button>
                  <button style={btnStyle(false)} onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <span style={{ color: '#664422', fontSize: '0.72rem' }}>
                  Lv {p.level_index} · ({p.x}, {p.y})
                  {p.boundary && ` · ${normalizeBoundaries(p.boundary).length} boundary zone(s)`}
                </span>
                <button style={btnStyle(false)} onClick={() => setEditing({
                  id: p.id,
                  level_index: p.level_index,
                  x: p.x,
                  y: p.y,
                  boundaries: normalizeBoundaries(p.boundary),
                })}>Edit</button>
                <button style={btnStyle(true)} onClick={() => handleDelete(p.id)}>Remove</button>
              </>
            )}
          </div>
        )
      })}

      {newForm ? (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
          <select style={selectStyle} value={newForm.npcId}
            onChange={(e) => setNewForm((f) => ({ ...f, npcId: e.target.value }))}>
            <option value="">— select NPC —</option>
            {allNpcs.map((n) => (
              <option key={n.id} value={n.id}>{n.name}</option>
            ))}
          </select>
          <label style={{ fontSize: '0.7rem', color: '#664422' }}>Level</label>
          <input style={inputStyle} type="number" value={newForm.level}
            onChange={(e) => setNewForm((f) => ({ ...f, level: e.target.value }))} />
          <label style={{ fontSize: '0.7rem', color: '#664422' }}>X</label>
          <input style={inputStyle} type="number" value={newForm.x}
            onChange={(e) => setNewForm((f) => ({ ...f, x: e.target.value }))} />
          <label style={{ fontSize: '0.7rem', color: '#664422' }}>Y</label>
          <input style={inputStyle} type="number" value={newForm.y}
            onChange={(e) => setNewForm((f) => ({ ...f, y: e.target.value }))} />
          <button style={btnStyle(false)} onClick={handleAdd}>Add</button>
          <button style={btnStyle(false)} onClick={() => setNewForm(null)}>Cancel</button>
        </div>
      ) : (
        <button style={{ ...btnStyle(false), marginTop: '0.5rem' }}
          onClick={() => setNewForm(NEW_FORM_DEFAULT)}>
          + Add NPC
        </button>
      )}
    </div>
  )
}
